/**
 * «Хозяйства» (`/farms`) — реестр района с картой (прежний «Реестр района»
 * `/overview`, план v2 §4) и рабочий вид «Сезон» переключателем.
 *
 * Порядок: шапка → 4 числа (последнее — фильтр «с сигналами») → карта 8/12 |
 * список 4/12 → строка «Культуры 2026 · Готовность данных» → полная таблица
 * «Реестр» (по умолчанию) / «Сезон». Массив — только фильтр и подпись (№85).
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, LandPlot, MapPin, Search, Siren } from 'lucide-react';
import { defineMessages, type PlainMessage } from '../../i18n/define';
import { UNIT_LABELS, useT } from '../../i18n/hooks';
import { PageHeader } from '../ui/PageHeader';
import { Card, Stack } from '../ui/Card';
import { KpiCard } from '../ui/KpiCard';
import { DataTable, type Column } from '../ui/DataTable';
import { Badge } from '../ui/Badge';
import { EvidenceMark } from '../ui/EvidenceMark';
import { Icon } from '../ui/Icon';
import { InfoPopover } from '../../components/InfoPopover';
import { ChoiceSelect } from '../ui/AnalysisControls';
import { AgroMap } from '../map/MapWidget';
import type { ColorBy } from '../map/paint';
import { DataGate } from './parts';
import { Skeleton, SkeletonKpi, SkeletonList, SkeletonTable } from '../ui/Skeleton';
import { isLoading } from '../ui/AsyncBlock';
import { useRouteSource } from '../data-source/provider';
import { FarmsWorklist } from './FarmsWorklist';
import { usePageTrail } from '../shell/ShellContext';
import { useTrackData } from '../data/useTrackData';
import { FILES } from '../data/loader';
import { countOf, dateShort, ha, num, WORDS } from '../lib/format';
import { cropLabel } from '../../lib/crops';
import { api, type ActorRow } from '../../api/client';
import { useRequest } from '../../api/useRequest';
import { useAnalysisContext } from '../../analysis/context';
import { contextKey, once, pageThrough } from '../data-source/api/dataset';
import { farmLandSet } from '../data-source/api/cards';
import { landOf } from '../data-source/api/overview';
import { microhaToHa } from '../data-source/api/compare';
import { toSituation } from '../data-source/api/situations';
import { useDistrictFarms } from './farmBenchParts';
import { SignalChips } from '../astra/problems';
import { MassifCell, type MassifLand } from './MassifCell';
import {
  countsFromSituation,
  cropCodes,
  filterRegistry,
  hasSignal,
  openAssignmentsByFarm,
  queueOutsideRegistry,
  signalsLayer,
  sortRegistry,
  type QuarantineValue,
  type RegistryRow,
  type RegistrySort,
} from './farmsRegistry';
import type { ContourMeta, Crop, Farm, Massif, QuarantineFile } from '../data/types';
import './pages.css';
import './registry.css';

const m = defineMessages({
  levelDistrict: { id: 'farm.registry.level.district', defaultMessage: 'Район', description: 'Уровень в хлебных крошках реестра хозяйств: район (хоким, менеджер)' },
  districtName: { id: 'farm.registry.districtName', defaultMessage: 'Бука', description: 'Название района в хлебных крошках (Букинский район); имя собственное' },
  levelSection: { id: 'farm.registry.level.section', defaultMessage: 'Раздел', description: 'Уровень в хлебных крошках: раздел приложения' },
  title: { id: 'farm.registry.title', defaultMessage: 'Хозяйства', description: 'Заголовок страницы и раздела «Хозяйства»; также название раздела в хлебных крошках и карточки со списком' },
  noData: { id: 'farm.registry.noData', defaultMessage: 'нет данных', description: 'Вместо числа в таблице и KPI реестра, когда значение не названо; честное «нет данных», а не 0; строчными' },
  farmFallback: { id: 'farm.registry.farmFallback', defaultMessage: 'Хозяйство №{id}', description: 'Имя хозяйства в реестре, когда в данных его нет; {id} — номер хозяйства' },
  sortProblems: { id: 'farm.registry.sort.problems', defaultMessage: 'С проблемами сверху', description: 'Вариант выпадающего списка «Порядок» в реестре хозяйств: хозяйства с сигналами выше' },
  sortName: { id: 'farm.registry.sort.name', defaultMessage: 'По имени', description: 'Вариант выпадающего списка «Порядок» в реестре хозяйств: по алфавиту' },
  sortLand: { id: 'farm.registry.sort.land', defaultMessage: 'По земле', description: 'Вариант выпадающего списка «Порядок» в реестре хозяйств: по площади земли' },
  allMassifs: { id: 'farm.registry.allMassifs', defaultMessage: 'Все массивы', description: 'Вариант выпадающего списка «Массив» над таблицей реестра: без фильтра по массиву' },
  colFarm: { id: 'farm.registry.col.farm', defaultMessage: 'Хозяйство', description: 'Колонка таблицы реестра хозяйств: хозяйство (имя — ссылка на карточку)' },
  colMassif: { id: 'farm.registry.col.massif', defaultMessage: 'Массив', description: 'Колонка таблицы реестра хозяйств: массив (основной и остальные)' },
  colLand: { id: 'farm.registry.col.land', defaultMessage: 'Земля', description: 'Колонка таблицы реестра хозяйств: площадь земли, га' },
  colContours: { id: 'farm.registry.col.contours', defaultMessage: 'Контуров', description: 'Колонка таблицы реестра хозяйств: число контуров; коротко' },
  colCrops: { id: 'farm.registry.col.crops', defaultMessage: 'Культуры (направление)', description: 'Колонка таблицы реестра хозяйств: объявленное направление хозяйства (культуры), а не размещение сезона' },
  cropsNone: { id: 'farm.registry.cropsNone', defaultMessage: 'не объявлены', description: 'Значение колонки «Культуры», когда хозяйство направление не объявляло; строчными' },
  colSignals: { id: 'farm.registry.col.signals', defaultMessage: 'Сигналы', description: 'Колонка таблицы реестра хозяйств: значки сигналов (сроки, вода, расхождения, карантин)' },
  signalsNone: { id: 'farm.registry.signalsNone', defaultMessage: 'нет', description: 'Значение колонки «Сигналы», когда сигналов нет; коротко' },
  colAssign: { id: 'farm.registry.col.assign', defaultMessage: 'Поручений', description: 'Колонка таблицы реестра хозяйств: число открытых поручений; коротко' },
  purpose: { id: 'farm.registry.purpose', defaultMessage: 'Реестр хозяйств района; сигналы — на {date}. Имя ведёт в карточку хозяйства.', description: 'Подзаголовок страницы «Хозяйства» (хоким, менеджер); {date} — дата среза' },
  purposeNoDate: { id: 'farm.registry.purposeNoDate', defaultMessage: 'Реестр хозяйств района; сигналы — на дату данных. Имя ведёт в карточку хозяйства.', description: 'Подзаголовок страницы «Хозяйства», когда дата среза ещё не известна' },
  evidenceDemo: { id: 'farm.registry.evidence.demo', defaultMessage: 'Срез с синтетикой демонстрации', description: 'Подпись значка класса данных в шапке реестра: в срез включены демонстрационные данные' },
  evidenceFact: { id: 'farm.registry.evidence.fact', defaultMessage: 'База района без демо-данных', description: 'Подпись значка класса данных в шапке реестра: только настоящие данные района' },
  kpiLoadFarms: { id: 'farm.registry.kpi.loadFarms', defaultMessage: 'число хозяйств', description: 'Что грузится (читает диктор «Загружаю …»): число хозяйств' },
  kpiFarms: { id: 'farm.registry.kpi.farms', defaultMessage: 'Хозяйств', description: 'Подпись KPI-карточки реестра: число хозяйств; коротко' },
  kpiFarmsFail: { id: 'farm.registry.kpi.farmsFail', defaultMessage: 'реестр хозяйств не прочитан', description: 'Нижняя строка KPI «Хозяйств» при ошибке загрузки реестра' },
  kpiFarmsNote: { id: 'farm.registry.kpi.farmsNote', defaultMessage: 'в реестре района', description: 'Нижняя строка KPI «Хозяйств»' },
  kpiLoadLand: { id: 'farm.registry.kpi.loadLand', defaultMessage: 'землю района', description: 'Что грузится (читает диктор «Загружаю …»): земля района' },
  kpiLand: { id: 'farm.registry.kpi.land', defaultMessage: 'Земля района', description: 'Подпись KPI-карточки реестра: сельхозземля района, га' },
  kpiLandNote: { id: 'farm.registry.kpi.landNote', defaultMessage: 'с/х земля по обзору района', description: 'Нижняя строка KPI «Земля района» (с/х — сельскохозяйственная)' },
  kpiLandNoData: { id: 'farm.registry.kpi.landNoData', defaultMessage: 'с/х земля: нет данных', description: 'Нижняя строка KPI «Земля района», когда площадь не названа' },
  kpiOverviewFail: { id: 'farm.registry.kpi.overviewFail', defaultMessage: 'обзор района не прочитан', description: 'Нижняя строка KPI, когда не загрузился обзор района' },
  kpiLoadContours: { id: 'farm.registry.kpi.loadContours', defaultMessage: 'контуры на карте', description: 'Что грузится (читает диктор «Загружаю …»): контуры на карте' },
  kpiContours: { id: 'farm.registry.kpi.contours', defaultMessage: 'Контуров на карте', description: 'Подпись KPI-карточки реестра: число контуров с геометрией на карте' },
  kpiContoursNote: { id: 'farm.registry.kpi.contoursNote', defaultMessage: 'из {count, plural, one {# контура} few {# контуров} many {# контуров} other {# контура}} реестра', description: 'Нижняя строка KPI «Контуров на карте»; {count} — всего контуров в реестре' },
  kpiLoadFlagged: { id: 'farm.registry.kpi.loadFlagged', defaultMessage: 'хозяйства с сигналами', description: 'Что грузится (читает диктор «Загружаю …»): хозяйства с сигналами' },
  kpiFlagged: { id: 'farm.registry.kpi.flagged', defaultMessage: 'Хозяйств с сигналами', description: 'Подпись KPI-карточки реестра: сколько хозяйств с хотя бы одним сигналом; нажатие включает фильтр' },
  kpiFlaggedOn: { id: 'farm.registry.kpi.flaggedOn', defaultMessage: 'фильтр включён — нажмите, чтобы снять', description: 'Нижняя строка KPI «Хозяйств с сигналами», когда фильтр включён' },
  kpiFlaggedOff: { id: 'farm.registry.kpi.flaggedOff', defaultMessage: 'нажмите — сузить список и карту', description: 'Нижняя строка KPI «Хозяйств с сигналами», когда фильтр выключен' },
  kpiFlaggedInfoLabel: { id: 'farm.registry.kpi.flaggedInfoLabel', defaultMessage: 'Как считаются «Хозяйства с сигналами»', description: 'aria-label кнопки-подсказки «i» у KPI «Хозяйств с сигналами»' },
  kpiFlaggedInfo: { id: 'farm.registry.kpi.flaggedInfo', defaultMessage: 'Хозяйства реестра хотя бы с одним сигналом: сроки (после срока или окно скоро закроется), вода, расхождения отчётов, карантин. {outside, plural, =0 {Совпадает с «Все» в блоке «Где нужно разобраться» на Обзоре.} one {Во вкладке «Все» блока «Где нужно разобраться» на Обзоре число больше: ещё # субъекта с сигналами нет в реестре хозяйств — они только в этой очереди.} few {Во вкладке «Все» блока «Где нужно разобраться» на Обзоре число больше: ещё # субъектов с сигналами нет в реестре хозяйств — они только в этой очереди.} many {Во вкладке «Все» блока «Где нужно разобраться» на Обзоре число больше: ещё # субъектов с сигналами нет в реестре хозяйств — они только в этой очереди.} other {Во вкладке «Все» блока «Где нужно разобраться» на Обзоре число больше: ещё # субъектов с сигналами нет в реестре хозяйств — они только в этой очереди.}}', description: 'Текст всплывающей подсказки у KPI «Хозяйств с сигналами» (хоким, менеджер); {outside} — сколько субъектов с сигналами есть в очереди «Где нужно разобраться», но не входят в реестр хозяйств (резерв, сводные строки дехканских хозяйств): 0 — числа совпадают, иначе подсказка называет разницу' },
  kpiFlaggedAction: { id: 'farm.registry.kpi.flaggedAction', defaultMessage: 'Хозяйства с сигналами: показать в списке и на карте только их', description: 'Подсказка нажимаемой KPI-карточки «Хозяйств с сигналами»: включить фильтр списка и карты' },
  mapTitle: { id: 'farm.registry.map.title', defaultMessage: 'Земля хозяйств на карте', description: 'Заголовок карточки с картой на странице «Хозяйства»' },
  mapOnlyFlagged: { id: 'farm.registry.map.onlyFlagged', defaultMessage: 'только с сигналами', description: 'Подпись справа в шапке карточки карты, когда включён фильтр по сигналам; строчными' },
  mapFocus: { id: 'farm.registry.map.focus', defaultMessage: 'Фокус: «{name}»', description: 'Подпись под картой: выбранное хозяйство; {name} — имя хозяйства' },
  cardNoSignals: { id: 'farm.registry.map.noSignals', defaultMessage: 'сигналов нет', description: 'Строка в карточке контура на карте хозяйств: у хозяйства сигналов нет' },
  cardAssignments: { id: 'farm.registry.map.assignments', defaultMessage: 'открытых поручений: {count}', description: 'Строка в карточке контура на карте хозяйств; {count} — число открытых поручений хозяйства' },
  listTitle: { id: 'farm.registry.list.title', defaultMessage: 'Хозяйства', description: 'Заголовок карточки со списком хозяйств рядом с картой' },
  listAside: { id: 'farm.registry.list.aside', defaultMessage: '{shown} из {total}', description: 'Подпись справа в шапке списка хозяйств: показано из всего; числа строкой' },
  searchPlaceholder: { id: 'farm.registry.search.placeholder', defaultMessage: 'Поиск хозяйства по имени', description: 'Подсказка внутри поля поиска хозяйств' },
  searchAria: { id: 'farm.registry.search.aria', defaultMessage: 'Поиск хозяйства по имени', description: 'aria-label поля поиска хозяйств' },
  listLoading: { id: 'farm.registry.list.loading', defaultMessage: 'реестр хозяйств', description: 'Что грузится (читает диктор «Загружаю …»): реестр хозяйств; также подпись скелетона таблицы' },
  listEmpty: { id: 'farm.registry.list.empty', defaultMessage: 'Хозяйств не нашлось.', description: 'Пустое состояние списка хозяйств' },
  listEmptyQuery: { id: 'farm.registry.list.emptyQuery', defaultMessage: 'Хозяйств не нашлось по запросу «{query}».', description: 'Пустое состояние списка хозяйств при поиске; {query} — введённый запрос' },
  rowTitle: { id: 'farm.registry.list.rowTitle', defaultMessage: 'Показать землю хозяйства на карте', description: 'Всплывающая подсказка строки списка хозяйств' },
  haValue: { id: 'farm.registry.haValue', defaultMessage: '{ha} га', description: 'Гектары с единицей; {ha} — число строкой' },
  listMore: { id: 'farm.registry.list.more', defaultMessage: 'ещё {count} — в таблице ниже', description: 'Строка под списком хозяйств: сколько хозяйств не поместилось; {count} — число строкой' },
  openFarm: { id: 'farm.registry.openFarm', defaultMessage: 'Открыть карточку хозяйства →', description: 'Ссылка под списком: открыть карточку выбранного хозяйства' },
  stripCrops: { id: 'farm.registry.strip.crops', defaultMessage: 'Культуры {season} по площади', description: 'Подпись группы в полосе под картой: культуры сезона по площади; {season} — год сезона' },
  stripCropsNoSeason: { id: 'farm.registry.strip.cropsNoSeason', defaultMessage: 'Культуры по площади', description: 'Подпись группы в полосе под картой, когда сезон неизвестен' },
  stripCropItem: { id: 'farm.registry.strip.cropItem', defaultMessage: '{name} <span>{ha} га</span>', description: 'Культура и её площадь в полосе под картой; {name} — культура, {ha} — число строкой; тег span не трогать' },
  stripNoCrops: { id: 'farm.registry.strip.noCrops', defaultMessage: 'в структуре посевов нет строк с площадью — пробел данных, а не ноль', description: 'Строка в полосе под картой, когда нет посевов с площадью' },
  stripOverviewFail: { id: 'farm.registry.strip.overviewFail', defaultMessage: 'обзор района не прочитан', description: 'Строка в полосе под картой при ошибке загрузки обзора района' },
  stripReady: { id: 'farm.registry.strip.ready', defaultMessage: 'Готовность данных', description: 'Подпись группы в полосе под картой: насколько данные района готовы' },
  stripGeometry: { id: 'farm.registry.strip.geometry', defaultMessage: 'геометрия <span>{have} из {total}</span>', description: 'Элемент полосы готовности: у скольких контуров есть геометрия; тег span не трогать' },
  stripFarms: { id: 'farm.registry.strip.farms', defaultMessage: 'хозяйств <span>{count}</span>', description: 'Элемент полосы готовности: число хозяйств; тег span не трогать' },
  stripHead: { id: 'farm.registry.strip.head', defaultMessage: 'глава хозяйства <span>{value}</span>', description: 'Элемент полосы готовности: данные о руководителе хозяйства; {value} — «нет данных»; тег span не трогать' },
  stripUnknown: { id: 'farm.registry.strip.unknown', defaultMessage: 'счёт неизвестен', description: 'Строка в полосе готовности данных, когда обзор недоступен' },
  tableSeason: { id: 'farm.registry.table.season', defaultMessage: 'Хозяйства агроцикла', description: 'Заголовок карточки с таблицей в виде «Сезон»' },
  tableRegistry: { id: 'farm.registry.table.registry', defaultMessage: 'Реестр хозяйств', description: 'Заголовок карточки с таблицей в виде «Реестр»' },
  tableAside: { id: 'farm.registry.table.aside', defaultMessage: 'реестр — текущее состояние', description: 'Подпись справа в шапке карточки с таблицей реестра' },
  tableInfo: { id: 'farm.registry.table.info', defaultMessage: 'Сигналы — очередь разбора сервера на {date}: хозяйство в «Все» по порогу существенности или в линзе своего типа (сроки, вода, расхождения, карантин — контуры землепользования в красном или жёлтом статусе или с предписанием после срока). Значки: сроки — работ на полях без отчёта после срока; вода — недоподача «−N м³» или число поливов контура, чьё окно закрылось без подачи; расхождения; карантин. «Культуры» — объявленное направление хозяйства, а не размещение сезона. Массив — основной (больше земли), «+N» — остальные с гектарами.', description: 'Текст всплывающей подсказки «i» карточки с таблицей реестра (хоким, менеджер); {date} — дата среза или «дату данных»' },
  tableInfoNoDate: { id: 'farm.registry.table.infoNoDate', defaultMessage: 'дату данных', description: 'Подставляется в подсказку таблицы реестра вместо даты, когда дата среза неизвестна («…очередь разбора сервера на дату данных»)' },
  viewLabel: { id: 'farm.registry.view.label', defaultMessage: 'Вид таблицы', description: 'aria-label переключателя «Реестр / Сезон» над таблицей' },
  viewRegistry: { id: 'farm.registry.view.registry', defaultMessage: 'Реестр', description: 'Кнопка переключателя вида таблицы: реестр (текущее состояние); коротко' },
  viewSeason: { id: 'farm.registry.view.season', defaultMessage: 'Сезон', description: 'Кнопка переключателя вида таблицы: сезон (факты по агроциклу); коротко' },
  filterMassif: { id: 'farm.registry.filter.massif', defaultMessage: 'Массив', description: 'Подпись выпадающего списка над таблицей реестра: фильтр по массиву' },
  filterOrder: { id: 'farm.registry.filter.order', defaultMessage: 'Порядок', description: 'Подпись выпадающего списка над таблицей реестра: порядок строк' },
  filterOnlyFlagged: { id: 'farm.registry.filter.onlyFlagged', defaultMessage: 'Только с сигналами', description: 'Флажок над таблицей реестра: показать только хозяйства с сигналами' },
  tableFail: { id: 'farm.registry.table.fail', defaultMessage: 'Реестр хозяйств не прочитан: {message}', description: 'Сообщение вместо таблицы при ошибке загрузки реестра; {message} — текст ошибки от сервера' },
  farmWord: { id: 'farm.registry.farmWord', defaultMessage: '{count, plural, one {хозяйство} few {хозяйства} many {хозяйств} other {хозяйства}}', description: 'Слово «хозяйство» без числа — знаменатель под таблицей реестра' },
  emptyTitle: { id: 'farm.registry.empty.title', defaultMessage: 'Хозяйств не нашлось', description: 'Заголовок пустой таблицы реестра хозяйств' },
  emptyFiltered: { id: 'farm.registry.empty.filtered', defaultMessage: 'по фильтру совпадений нет — снимите фильтр', description: 'Пояснение пустой таблицы реестра при активном фильтре или поиске' },
  emptyRegistry: { id: 'farm.registry.empty.registry', defaultMessage: 'реестр пуст', description: 'Пояснение пустой таблицы реестра без фильтров' },
  // фикстурный вид
  fxPurpose: { id: 'farm.registry.fx.purpose', defaultMessage: 'Реестр хозяйств среза. Спуск на уровень хозяйства — по его имени.', description: 'Подзаголовок страницы «Хозяйства» в режиме учебных данных' },
  fxKpiFarmsNote: { id: 'farm.registry.fx.kpiFarmsNote', defaultMessage: 'в реестре среза', description: 'Нижняя строка KPI «Хозяйств» в режиме учебных данных' },
  fxKpiLand: { id: 'farm.registry.fx.kpiLand', defaultMessage: 'Земля хозяйств', description: 'Подпись KPI-карточки в режиме учебных данных: земля всех хозяйств, га' },
  fxKpiLandNote: { id: 'farm.registry.fx.kpiLandNote', defaultMessage: 'реестр не прочитан', description: 'Нижняя строка KPI «Земля хозяйств», когда реестр не прочитан' },
  fxColMassifs: { id: 'farm.registry.fx.col.massifs', defaultMessage: 'Массивы', description: 'Колонка таблицы хозяйств в режиме учебных данных: массивы хозяйства' },
  fxColDirection: { id: 'farm.registry.fx.col.direction', defaultMessage: 'Направление', description: 'Колонка таблицы хозяйств в режиме учебных данных: направление хозяйства' },
  fxDirectionNone: { id: 'farm.registry.fx.directionNone', defaultMessage: 'направление не объявлено', description: 'Значение колонки «Направление», когда оно не объявлено; строчными' },
  fxColContours: { id: 'farm.registry.fx.col.contours', defaultMessage: 'Контуры', description: 'Колонка таблицы хозяйств в режиме учебных данных: число контуров' },
  fxGate: { id: 'farm.registry.fx.gate', defaultMessage: 'реестр ждёт farms.json', description: 'Сообщение «не удалось показать», когда нет файла учебных данных; farms.json — имя файла, не переводить' },
  fxEmptyQuery: { id: 'farm.registry.fx.emptyQuery', defaultMessage: 'по запросу «{query}» совпадений нет — измените поиск', description: 'Пояснение пустой таблицы хозяйств при поиске; {query} — введённый запрос' },
  fxEmptyNone: { id: 'farm.registry.fx.emptyNone', defaultMessage: 'в срезе не оказалось ни одной записи', description: 'Пояснение пустой таблицы хозяйств без поиска' },
});

const LIST_ROWS = 60;

/**
 * Линзы очереди, из которых собираются сигналы реестра (карантин — 6al.2).
 * «Сроки» (`deadlines`) — после срока ИЛИ окно закроется скоро: линза `window`
 * видит только второе, и хозяйства с одной просрочкой ниже порога выпадали из
 * реестра (276 против 309 в «Где нужно разобраться», В7, 07.10).
 */
const SIGNAL_LENSES = ['all', 'deadlines', 'water', 'verification', 'quarantine'] as const;

const SORTS: { id: RegistrySort; name: PlainMessage }[] = [
  { id: 'problems', name: m.sortProblems },
  { id: 'name', name: m.sortName },
  { id: 'land', name: m.sortLand },
];

export function Farms() {
  const t = useT();
  usePageTrail([
    { level: t(m.levelDistrict), name: t(m.districtName), to: '/' },
    { level: t(m.levelSection), name: t(m.title) },
  ]);
  const canon = useRouteSource()?.source === 'api';
  return canon ? <FarmsRegistry /> : <FarmsFixtures />;
}

/* ------------------------------------------------------------------ канон */

function FarmsRegistry() {
  const t = useT();
  const noData = t(m.noData);
  const { params, asOf: ctxAsOf } = useAnalysisContext();
  const [search, setSearch] = useSearchParams();
  // `?sort=` — ссылка «Все хозяйства» с Обзора ведёт в вид «Сезон».
  const view = search.get('view') === 'season' || search.get('sort') ? 'season' : 'registry';
  const massifId = search.get('massif');
  const onlyFlagged = search.get('signals') === '1';
  const sortParam = search.get('rsort');
  const sort: RegistrySort = SORTS.find((s) => s.id === sortParam)?.id ?? 'problems';
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [layer, setLayer] = useState<ColorBy>('crop');

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(search);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setSearch(next, { replace: true });
  };

  const district = useDistrictFarms();
  const farms = useTrackData<Farm[]>(FILES.farms);
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const contours = useTrackData<ContourMeta[]>(FILES.contours);
  const quarantine = useTrackData<QuarantineFile>(FILES.quarantine);
  const crops = useTrackData<Crop[]>(FILES.crops);
  const actors = useRequest(
    () => once(`actors:${contextKey(params)}`, () => pageThrough<ActorRow>((limit, offset) => api.actors({ ...params, limit, offset }))),
    [params],
  );
  // Ход кампаний по массивам экран не читает — это ~1,4 МБ из ~1,6 МБ ответа (журнал №442).
  const overview = useRequest(() => api.districtOverview({ ...params, massif_progress: false }), [params]);
  const season = district.cycle ? district.cycle.split(':')[0] : null;
  /**
   * Сигналы хозяйств — с сервера: строки очереди по всем линзам («Все» по
   * порогу, и линзы сроков, воды, расхождений, карантина — при любом числе).
   * Одна строка хозяйства во всех линзах одинакова — сливаем по хозяйству.
   */
  const situations = useRequest(
    () =>
      season
        ? Promise.all(
            SIGNAL_LENSES.map((lens) =>
              once(`situations:${lens}:${season}:${contextKey(params)}`, () => api.situations({ ...params, season, lens, limit: 500 })),
            ),
          )
        : Promise.resolve(null),
    [params, season],
  );
  const assignments = useRequest(() => api.assignments(params).catch(() => null), [params]);

  const massifName = useMemo(() => new Map((massifs.data ?? []).map((m) => [m.id, m.name])), [massifs.data]);
  const farmMassifs = useMemo(() => new Map((farms.data ?? []).map((f) => [f.id, f.massif_ids])), [farms.data]);
  /** Земля хозяйства по массивам (по контурам реестра), от большей к меньшей. */
  const farmMassifLand = useMemo(() => {
    const byFarm = new Map<string, Map<string, number | null>>();
    for (const c of contours.data ?? []) {
      if (!c.farm_id || !c.massif_id) continue;
      const m = byFarm.get(c.farm_id) ?? new Map<string, number | null>();
      const add = c.area_stated === false ? null : c.area_ha;
      m.set(c.massif_id, add === null ? (m.get(c.massif_id) ?? null) : (m.get(c.massif_id) ?? 0) + add);
      byFarm.set(c.farm_id, m);
    }
    return byFarm;
  }, [contours.data]);
  const contourFarm = useMemo(() => {
    const out = new Map<string, string>();
    for (const c of contours.data ?? []) if (c.farm_id) out.set(c.id, c.farm_id);
    return out;
  }, [contours.data]);
  const qContours = useMemo(() => {
    const out = new Map<string, QuarantineValue>();
    for (const s of quarantine.data?.statuses ?? []) out.set(s.contour_id, s.status as QuarantineValue);
    return out;
  }, [quarantine.data]);
  const situationByFarm = useMemo(() => {
    const out = new Map<string, ReturnType<typeof toSituation>>();
    for (const list of situations.data ?? []) {
      for (const row of list.items) {
        const s = toSituation(row);
        if (!out.has(s.farmId)) out.set(s.farmId, s);
      }
    }
    return out;
  }, [situations.data]);
  const openByFarm = useMemo(() => (assignments.data ? openAssignmentsByFarm(assignments.data.items) : null), [assignments.data]);

  const rows = useMemo<RegistryRow[]>(
    () =>
      (actors.data?.items ?? []).map((a) => {
        const id = String(a.id);
        const mids = farmMassifs.get(id) ?? [];
        const s = situationByFarm.get(id);
        const signals = countsFromSituation(s, s ? s.quarantine ?? 0 : 0);
        return {
          id,
          name: a.name ?? t(m.farmFallback, { id: a.id }),
          massifIds: mids,
          massifNames: mids.map((m) => massifName.get(m) ?? m),
          massifLand: (() => {
            const land = farmMassifLand.get(id) ?? new Map<string, number | null>();
            const ids = [...new Set([...land.keys(), ...mids])];
            return ids
              .map((m): MassifLand => ({ id: m, name: massifName.get(m) ?? m, ha: land.get(m) ?? null }))
              .sort((a, b) => (b.ha ?? -1) - (a.ha ?? -1) || a.name.localeCompare(b.name, 'ru'));
          })(),
          areaHa: microhaToHa(a.total_microha),
          contours: a.contours,
          crops: cropCodes(a.directions),
          signals,
          flagged: hasSignal(signals),
          openAssignments: openByFarm ? (openByFarm.get(id) ?? 0) : null,
        };
      }),
    [actors.data, farmMassifs, farmMassifLand, massifName, situationByFarm, openByFarm, t],
  );
  const flaggedCount = rows.filter((r) => r.flagged).length;
  // Субъекты очереди вне реестра хозяйств (резерв, сводные строки дехкан): в
  // «Где нужно разобраться» они есть, в реестре — нет. Число — из тех же
  // ответов, что и «с сигналами» (agroai-b48.3).
  const outsideRegistry = useMemo(
    () => queueOutsideRegistry(new Set((actors.data?.items ?? []).map((a) => String(a.id))), situationByFarm.keys()),
    [actors.data, situationByFarm],
  );
  const shown = useMemo(
    () => sortRegistry(filterRegistry(rows, { query, massifId, onlyFlagged }), sort),
    [rows, query, massifId, onlyFlagged, sort],
  );
  const rowById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  /* ---- карта ---- */
  const selectedLand = useTrackData<ContourMeta[]>(selected ? farmLandSet(selected) : null);
  const focusIds = useMemo(() => (selected && selectedLand.data ? selectedLand.data.map((c) => c.id) : undefined), [selected, selectedLand.data]);
  const values = useMemo(() => signalsLayer(rows, contourFarm, qContours), [rows, contourFarm, qContours]);
  const flaggedIds = useMemo(() => {
    if (!onlyFlagged && !massifId) return undefined;
    const keep = new Set(shown.map((r) => r.id));
    const ids: string[] = [];
    for (const [c, f] of contourFarm) if (keep.has(f)) ids.push(c);
    return ids;
  }, [onlyFlagged, massifId, shown, contourFarm]);
  const colorBy: ColorBy = onlyFlagged ? 'values' : layer;

  /* ---- строка культур и готовности ---- */
  const ov = overview.data;
  const agriLand = ov ? landOf(ov.land, 'AGRICULTURAL_LAND_TOTAL') : null;
  const registry = useMemo(() => {
    const ms = ov?.massifs ?? [];
    return { contours: ms.reduce((s, m) => s + m.contours, 0), withGeometry: ms.reduce((s, m) => s + m.contours_with_geometry, 0) };
  }, [ov]);
  const cropSlices = useMemo(
    () =>
      (ov?.crops.metrics ?? [])
        .filter((m) => m.level === 'crop' && m.area.value !== null)
        .map((m) => ({ code: m.code, name: cropLabel(m.code).known ? cropLabel(m.code).label : (m.name ?? m.code), ha: microhaToHa(m.area.value) ?? 0 }))
        .sort((a, b) => b.ha - a.ha),
    [ov],
  );
  const asOf = ctxAsOf ?? district.asOf;

  const toggleFlagged = () => {
    setParam('signals', onlyFlagged ? null : '1');
  };

  const massifOptions = useMemo(
    () => [{ id: '', name: t(m.allMassifs) }, ...[...(massifs.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'ru')).map((m) => ({ id: m.id, name: m.name }))],
    [massifs.data, t],
  );
  const sortOptions = SORTS.map((s) => ({ id: s.id, name: t(s.name) }));

  const columns: Column<RegistryRow>[] = [
    { key: 'name', header: t(m.colFarm), width: '220px', render: (r) => <Link to={`/farms/${r.id}`}>{r.name}</Link> },
    { key: 'massif', header: t(m.colMassif), render: (r) => <MassifCell list={r.massifLand ?? []} farmName={r.name} /> },
    { key: 'area', header: t(m.colLand), unit: t(UNIT_LABELS.ha), numeric: true, render: (r) => (r.areaHa === null ? noData : ha(r.areaHa)) },
    { key: 'contours', header: t(m.colContours), numeric: true, render: (r) => num(r.contours) },
    {
      key: 'crops',
      header: t(m.colCrops),
      render: (r) =>
        r.crops.length ? (
          <span className="farms-crops">{r.crops.map((c) => <Badge key={c} tone="outline">{cropLabel(c).label}</Badge>)}</span>
        ) : (
          <span className="t-caption">{t(m.cropsNone)}</span>
        ),
    },
    { key: 'signals', header: t(m.colSignals), render: (r) => (r.flagged ? <SignalChips counts={r.signals} /> : <span className="t-caption">{t(m.signalsNone)}</span>) },
    { key: 'assign', header: t(m.colAssign), numeric: true, render: (r) => (r.openAssignments === null ? noData : num(r.openAssignments)) },
  ];

  // Пока очередь не пришла, счёт «с сигналами» был бы одним карантином — не показываем.
  // Реестр нужен счёту наравне с очередью: без него «с сигналами» — 0, а «вне реестра» — вся очередь.
  const loadingSignals = (!situations.data && situations.state !== 'error') || isLoading(actors);

  /**
   * Поиск и список хозяйств — панель управления карты: на странице справа от
   * карты, в развёрнутой карте — её боковой панелью (журнал №1390).
   */
  const farmList = (compact: boolean) => (
    <div className={compact ? 'farms-list farms-list--compact' : 'farms-list'} data-guide="list">
      <label className="searchbox"><span className="searchbox__field"><Icon icon={Search} size={16} /><input type="search" placeholder={t(m.searchPlaceholder)} aria-label={t(m.searchAria)} value={query} onChange={(e) => setQuery(e.target.value)} /></span></label>
      {/* Порядок и значки строк зависят от сигналов: до них — скелетон, а не
          список, который потом перестроится (6al.49). */}
      {actors.state === 'loading' || isLoading(situations) ? (
        <SkeletonList label={t(m.listLoading)} items={5} tone="soft" />
      ) : shown.length === 0 ? (
        <p className="t-caption">{query ? t(m.listEmptyQuery, { query }) : t(m.listEmpty)}</p>
      ) : (
        <ul className="farms-list__rows">
          {shown.slice(0, LIST_ROWS).map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="farms-list__row"
                aria-pressed={selected === r.id}
                onClick={() => setSelected(selected === r.id ? null : r.id)}
                title={t(m.rowTitle)}
              >
                <span className="farms-list__name">{r.name}</span>
                <span className="farms-list__ha">{r.areaHa === null ? noData : t(m.haValue, { ha: ha(r.areaHa) })}</span>
                {r.flagged ? <span className="farms-list__chips"><SignalChips counts={r.signals} /></span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      {shown.length > LIST_ROWS ? <span className="farms-list__more">{t(m.listMore, { count: num(shown.length - LIST_ROWS) })}</span> : null}
      {selected ? <Link to={`/farms/${selected}`}>{t(m.openFarm)}</Link> : null}
    </div>
  );

  return (
    <Stack>
      <PageHeader
        level="district"
        title={t(m.title)}
        purpose={asOf ? t(m.purpose, { date: dateShort(asOf) }) : t(m.purposeNoDate)}
        actions={<EvidenceMark kind={params.include_demo ? 'demo' : 'fact'} label={params.include_demo ? t(m.evidenceDemo) : t(m.evidenceFact)} />}
      />

      <div className="farms-kpis" data-guide="kpi">
        {isLoading(actors) ? <SkeletonKpi label={t(m.kpiLoadFarms)} /> : (
        <KpiCard
          label={t(m.kpiFarms)}
          icon={Building2}
          value={actors.data ? num(actors.data.total) : '—'}
          note={actors.state === 'error' ? t(m.kpiFarmsFail) : t(m.kpiFarmsNote)}
        />
        )}
        {isLoading(overview) ? <SkeletonKpi label={t(m.kpiLoadLand)} /> : (
        <KpiCard
          label={t(m.kpiLand)}
          icon={LandPlot}
          value={agriLand?.ha != null ? (agriLand.ha >= 10_000 ? num(Math.round(agriLand.ha)) : ha(agriLand.ha)) : '—'}
          unit={agriLand?.ha != null ? t(UNIT_LABELS.ha) : undefined}
          note={ov ? (agriLand?.ha != null ? t(m.kpiLandNote) : t(m.kpiLandNoData)) : t(m.kpiOverviewFail)}
        />
        )}
        {isLoading(overview) ? <SkeletonKpi label={t(m.kpiLoadContours)} /> : (
        <KpiCard
          label={t(m.kpiContours)}
          icon={MapPin}
          value={ov ? num(registry.withGeometry) : '—'}
          note={ov ? t(m.kpiContoursNote, { count: registry.contours }) : t(m.kpiOverviewFail)}
        />
        )}
        {loadingSignals ? <SkeletonKpi label={t(m.kpiLoadFlagged)} /> : (
        <KpiCard
          label={t(m.kpiFlagged)}
          icon={Siren}
          value={num(flaggedCount)}
          note={onlyFlagged ? t(m.kpiFlaggedOn) : t(m.kpiFlaggedOff)}
          info={<InfoPopover icon="info" label={t(m.kpiFlaggedInfoLabel)}>{t(m.kpiFlaggedInfo, { outside: outsideRegistry })}</InfoPopover>}
          action="filter"
          pressed={onlyFlagged}
          onClick={toggleFlagged}
          actionLabel={t(m.kpiFlaggedAction)}
        />
        )}
      </div>

      <div className="farms-split">
        <Card size="lg" title={t(m.mapTitle)} aside={selected ? rowById.get(selected)?.name : onlyFlagged ? t(m.mapOnlyFlagged) : null}>
          <div data-guide="map">
          <AgroMap
            colorBy={colorBy}
            layersPanel
            layers={['crop', 'values']}
            onColorByChange={(next) => { setLayer(next); if (onlyFlagged && next !== 'values') setParam('signals', null); }}
            crops={crops.data ?? undefined}
            data={{ values }}
            filterIds={flaggedIds}
            focusIds={focusIds ?? flaggedIds}
            mode="frame"
            height={400}
            legend="collapsed"
            caption={selected ? t(m.mapFocus, { name: rowById.get(selected)?.name ?? selected }) : undefined}
            panel={{
              title: t(m.listTitle),
              summary: selected ? rowById.get(selected)?.name ?? selected : isLoading(actors) ? undefined : t(m.listAside, { shown: num(shown.length), total: num(rows.length) }),
              content: farmList(true),
            }}
            cardExtra={(c) => {
              const r = c.farmId ? rowById.get(c.farmId) : undefined;
              if (!r) return null;
              return (
                <div className="farms-strip__group">
                  {r.flagged ? <SignalChips counts={r.signals} /> : <span className="t-caption">{t(m.cardNoSignals)}</span>}
                  {r.openAssignments ? <span className="t-caption">{t(m.cardAssignments, { count: num(r.openAssignments) })}</span> : null}
                </div>
              );
            }}
          />
          </div>
        </Card>

        <Card size="lg" title={t(m.listTitle)} aside={isLoading(actors) ? null : t(m.listAside, { shown: num(shown.length), total: num(rows.length) })}>
          {farmList(false)}
        </Card>
      </div>

      <Card tone="soft">
        <div className="farms-strip" data-guide="strip">
          <span className="farms-strip__group">
            <span className="farms-strip__label">{season ? t(m.stripCrops, { season }) : t(m.stripCropsNoSeason)}</span>
            {ov ? (
              cropSlices.length ? (
                cropSlices.slice(0, 6).map((c) => (
                  <span key={c.code} className="farms-strip__item">{t(m.stripCropItem, { name: c.name, ha: ha(c.ha), span: (chunks) => <span>{chunks}</span> })}</span>
                ))
              ) : (
                <span className="t-caption">{t(m.stripNoCrops)}</span>
              )
            ) : (
              overview.state === 'loading' || isLoading(overview) ? <Skeleton width={320} className="skel--caption" /> : <span className="t-caption">{t(m.stripOverviewFail)}</span>
            )}
          </span>
          <span className="farms-strip__group">
            <span className="farms-strip__label">{t(m.stripReady)}</span>
            {ov ? (
              <>
                <span className="farms-strip__item">{t(m.stripGeometry, { have: num(registry.withGeometry), total: num(registry.contours), span: (chunks) => <span>{chunks}</span> })}</span>
                <span className="farms-strip__item">{t(m.stripFarms, { count: actors.data ? num(actors.data.total) : noData, span: (chunks) => <span>{chunks}</span> })}</span>
                <span className="farms-strip__item">{t(m.stripHead, { value: noData, span: (chunks) => <span>{chunks}</span> })}</span>
              </>
            ) : (
              isLoading(overview) ? <Skeleton width={360} className="skel--caption" /> : <span className="t-caption">{t(m.stripUnknown)}</span>
            )}
          </span>
        </div>
      </Card>

      <Card
        size="lg"
        title={view === 'season' ? t(m.tableSeason) : t(m.tableRegistry)}
        aside={view === 'season' ? district.cycleName : t(m.tableAside)}
        info={view === 'season' ? undefined : t(m.tableInfo, { date: asOf ? dateShort(asOf) : t(m.tableInfoNoDate) })}
      >
        <div data-guide="table">
        <div className="farms-tools">
          <div className="farms-view" role="group" aria-label={t(m.viewLabel)}>
            <button type="button" aria-pressed={view === 'registry'} onClick={() => { const next = new URLSearchParams(search); ['view', 'sort', 'dir'].forEach((k) => next.delete(k)); setSearch(next, { replace: true }); }}>{t(m.viewRegistry)}</button>
            <button type="button" aria-pressed={view === 'season'} onClick={() => setParam('view', 'season')}>{t(m.viewSeason)}</button>
          </div>
          {view === 'registry' ? (
            <>
              <ChoiceSelect label={t(m.filterMassif)} value={massifId ?? ''} options={massifOptions} onChange={(v) => setParam('massif', v || null)} />
              <ChoiceSelect label={t(m.filterOrder)} value={sort} options={sortOptions} onChange={(v) => setParam('rsort', v === 'problems' ? null : v)} />
              <label className="fb-toolbar">
                <input type="checkbox" checked={onlyFlagged} onChange={toggleFlagged} /> {t(m.filterOnlyFlagged)}
              </label>
            </>
          ) : null}
        </div>
        {view === 'season' ? (
          <FarmsWorklist />
        ) : isLoading(actors) ? (
          <SkeletonTable label={t(m.listLoading)} rows={8} cols={7} />
        ) : actors.state === 'error' ? (
          <p className="t-caption">{t(m.tableFail, { message: actors.error?.message ?? '' })}</p>
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={shown}
              getKey={(r) => r.id}
              limit={15}
              denominator={m.farmWord}
              hardCap={1000}
              empty={{ title: t(m.emptyTitle), reason: query || massifId || onlyFlagged ? t(m.emptyFiltered) : t(m.emptyRegistry) }}
            />
          </>
        )}
        </div>
      </Card>
    </Stack>
  );
}

/* ---------------------------------------------------------------- фикстуры */

function FarmsFixtures() {
  const t = useT();
  const farms = useTrackData<Farm[]>(FILES.farms);
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const [query, setQuery] = useState('');
  const massifNameById = useMemo(() => new Map((massifs.data ?? []).map((m) => [m.id, m.name])), [massifs.data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = farms.data ?? [];
    return q ? rows.filter((f) => f.name.toLowerCase().includes(q)) : rows;
  }, [farms.data, query]);
  const totalArea = (farms.data ?? []).reduce((s, f) => s + f.area_ha, 0);
  const columns: Column<Farm>[] = [
    { key: 'name', header: t(m.colFarm), render: (row) => <Link to={`/farms/${row.id}`}>{row.name}</Link> },
    { key: 'massifs', header: t(m.fxColMassifs), render: (row) => row.massif_ids.map((m) => massifNameById.get(m) ?? m).join(', ') },
    { key: 'direction', header: t(m.fxColDirection), render: (row) => row.direction || t(m.fxDirectionNone) },
    { key: 'contours', header: t(m.fxColContours), numeric: true, render: (row) => num(row.contours) },
    { key: 'area', header: t(m.colLand), unit: t(UNIT_LABELS.ha), numeric: true, render: (row) => ha(row.area_ha) },
  ];
  return (
    <Stack>
      <PageHeader level="district" title={t(m.title)} purpose={t(m.fxPurpose)} />
      <div className="farms-kpis" data-guide="kpi">
        <KpiCard label={t(m.kpiFarms)} value={farms.data ? num(farms.data.length) : '—'} note={t(m.fxKpiFarmsNote)} />
        <KpiCard label={t(m.fxKpiLand)} value={farms.data ? ha(totalArea) : '—'} unit={t(UNIT_LABELS.ha)} note={farms.data ? countOf(farms.data.length, WORDS.farm) : t(m.fxKpiLandNote)} />
      </div>
      <Card size="lg">
        <div data-guide="table">
        <div className="toolbar">
          <label className="searchbox"><span className="searchbox__field"><Icon icon={Search} size={16} /><input type="search" placeholder={t(m.searchPlaceholder)} aria-label={t(m.searchAria)} value={query} onChange={(e) => setQuery(e.target.value)} /></span></label>
        </div>
        <DataGate res={farms} what={t(m.fxGate)} skeleton={<SkeletonTable label={t(m.listLoading)} rows={8} cols={5} />}>
          {() => (
            <DataTable
              columns={columns}
              rows={filtered}
              getKey={(row) => row.id}
              denominator={m.farmWord}
              empty={{ title: t(m.emptyTitle), reason: query ? t(m.fxEmptyQuery, { query }) : t(m.fxEmptyNone) }}
            />
          )}
        </DataGate>
        </div>
      </Card>
    </Stack>
  );
}
